'use client';

import { useInbox } from '@shared/providers';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { LinkNavigationMenuItemProps } from '../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../NavigationMenu/NavigationMenu';

type LinkDefinition = {
  href: string;
  labelKey: string;
  trackName: string;
  trackContainer: string;
  testId: string;
  showInboxNotification?: boolean;
};

type BuildItemContext = {
  t: (key: string) => string;
  unreadMessagesCount: number;
  showNotificationBadge: boolean;
};

function buildNavigationItem(definition: LinkDefinition, context: BuildItemContext): LinkNavigationMenuItemProps {
  const item: LinkNavigationMenuItemProps = {
    href: definition.href,
    label: context.t(definition.labelKey),
    variant: 'link',
    'data-track-name': definition.trackName,
    'data-track-container': definition.trackContainer,
    'data-testid': definition.testId,
  };

  if (definition.showInboxNotification && context.showNotificationBadge) {
    item.notificationBubbleText = context.unreadMessagesCount.toString();
  }

  return item;
}

type MainNavigationLinksProps = {
  className?: string;
  linkDefinitions: Record<string, LinkDefinition>;
  links: readonly string[];
  namespace: string;
};

function MainNavigationLinks({ className, linkDefinitions, links, namespace }: MainNavigationLinksProps) {
  const t = useTranslations(namespace);
  const { unreadMessagesCount, showNotificationBadge } = useInbox();

  const items = useMemo(
    () =>
      links.flatMap(id => {
        const definition = linkDefinitions[id];
        if (!definition) return [];
        return [buildNavigationItem(definition, { t, unreadMessagesCount, showNotificationBadge })];
      }),
    [linkDefinitions, links, t, unreadMessagesCount, showNotificationBadge]
  );

  return <NavigationMenu items={items} className={className} />;
}

export { MainNavigationLinks };
export type { LinkDefinition, MainNavigationLinksProps };
